<template>
  <div class="drawer-root">
    <div class="drawer-header py-3 px-6">
      <div class="flex align-center justify-between mb-1">
        <div class="flex align-end gap-2">
          <div class="text-24 text-bold">
            AI 貿易書類照合
          </div>
          <div class="text-14 text-gray-600 ml-2">{{ supportedCombinationMessage }}</div>
        </div>
        <div class="flex gap-2">
          <el-button @click="handleClose" type="default" icon="el-icon-close">
            閉じる
          </el-button>
        </div>
      </div>
      <!-- 比較機能の説明 -->
      <div class="text-14 text-gray-700">
        比較したい帳票を2つ以上選択してください（最大{{ maxSelectableDocuments }}件）
      </div>
    </div>

    <div class="drawer-body flex py-3 px-3">
      <div class="content-area p-5">
        <!-- ボタンエリア -->
        <div class="action-buttons-area flex gap-8 mb-3">
          <el-button
            @click="handleCompareStart"
            type="primary"
            :disabled="!isCompareStartEnabled"
            :loading="isComparing"
          >
            比較開始（2書類）
          </el-button>

          <!-- 複数書類比較ボタン -->
          <el-button
            v-if="selectedDocuments.length >= 3"
            @click="handleCompareMultiStart"
            type="success"
            :disabled="!isCompareMultiStartEnabled"
            :loading="isComparing"
            icon="el-icon-s-grid"
          >
            {{ multiCompareButtonLabel }}
          </el-button>

          <el-button @click="handleRefresh" type="normal" icon="el-icon-refresh">
            更新
          </el-button>
        </div>
        <!-- 選択状態の表示 -->
        <div class="px-2 mb-4 text-12">
          <div
            v-if="selectedDocuments.length > 0"
            class="flex align-center mb-1"
          >
            <span class="text-primary text-bold mr-3">
              {{ selectionGuidanceMessage }}
            </span>
            <el-button
              type="text"
              size="mini"
              icon="el-icon-close"
              @click="handleClearSelection"
            >
              選択をクリア
            </el-button>
          </div>
          <div class="text-gray-800">
            全{{ tableData.length }}件表示中 ( 対象の貿易書類のみ )
            <span class="text-10 text-gray-600 ml-2">※帳票タイプが複数選択されているファイル、またはPDF以外のファイルは表示されません</span>
          </div>
        </div>

        <TwTable
          :tableData="tableData"
          :cols="cols"
          :loading="loading"
          :offsetY="600"
          :autoHeight="windowHeight - 600 < 200"
          :row-class-name="getRowClassName"
          @click-row="handleRowClick"
        >
          <!-- チェックボックスのカスタムスロット -->
          <template v-slot:selection="{ row }">
            <div @click.stop>
              <el-checkbox
                :value="isSelected(row)"
                :disabled="!isSelectable(row)"
                @change="(checked) => handleCheckboxChange(row, checked)"
              />
            </div>
          </template>

          <!-- ファイル名のカスタム表示（クリックでPDFプレビュー） -->
          <template v-slot:documentName="{ row }">
            <span
              class="document-name-link"
              @click.stop="handlePreviewPdf(row)"
            >
              {{ row.documentName }}
            </span>
          </template>

          <!-- AI-OCR処理状況のカスタム表示 -->
          <template v-slot:aiOcrStatus="{ row }">
            <!-- ステータスが0（未読取）かつOCR対象の場合のみボタン表示 -->
            <el-button
              v-if="row.aiOcrStatus === '読取開始' && row.canStartOcr"
              size="small"
              type="primary"
              :loading="ocrStartLoading[row.documentManageId]"
              @click="handleStartAiOcr(row)"
            >
              読取開始
            </el-button>
            <!-- それ以外はラベル表示 -->
            <el-tag
              v-else
              :type="getOcrStatusTagType(row.aiOcrStatus)"
              effect="plain"
            >
              {{ row.aiOcrStatus }}
            </el-tag>
          </template>
        </TwTable>

        <!-- ファイルアップロードセクション -->
        <AiOcrFileUpload
          @registered="handleRegistered"
          @save="(action, documents) => $emit('save', action, documents)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { PROCESS_TYPE } from 'lib-tw-common';
import TwTable from '@/components/vue3/TwTable.vue';
import { usePdfPreview } from '@/composables/usePdfPreview';
import AiOcrFileUpload from './AiOcrFileUpload.vue';

// AI-OCRサポート対象の帳票設定（プロセスタイプごと）
const ocrSupportedDocsConfig = {
  [PROCESS_TYPE.ICREQ]: {
    documentLinkInv: true,
    documentLinkBl: true,
    documentLinkPl: true,
    documentLinkPo: true,
  },
  [PROCESS_TYPE.ECREQ]: {
    documentLinkInv: true,
    documentLinkBl: true,
    documentLinkPl: true,
    documentLinkPo: true,
  },
};

// ドキュメントがOCR対象かどうか判定（mixinTwDocumentsAiOcr.jsのisOcrSupportedDocumentTypeと同じロジック）
const isOcrSupportedDocumentType = (doc) => {
  // documentLink*** のキーだけを抽出
  const linkKeys = Object.keys(doc).filter((k) => k.startsWith('documentLink'));

  // 1になっているキーを抽出
  const activeLinks = linkKeys.filter((k) => doc[k] === 1);

  // 条件:
  // 1. 有効Linkが1つだけ
  // 2. そのキーが OCR対象の種別
  return (
    activeLinks.length === 1 &&
    ocrSupportedDocsConfig[doc.processType]?.[activeLinks[0]]
  );
};

// ファイルサイズをフォーマット（バイトからKB/MBへ）
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// ISO 8601形式の日付文字列をJSTに変換してフォーマット（YYYY-MM-DD HH:MM）
const formatDateToJST = (isoDateString) => {
  if (!isoDateString) return '';

  const date = new Date(isoDateString);

  // JSTで表示（Asia/Tokyoタイムゾーン）
  const jstDate = new Date(
    date.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }),
  );

  const year = jstDate.getFullYear();
  const month = String(jstDate.getMonth() + 1).padStart(2, '0');
  const day = String(jstDate.getDate()).padStart(2, '0');
  const hours = String(jstDate.getHours()).padStart(2, '0');
  const minutes = String(jstDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// JSONデータをテーブル用に変換 & フィルタリング
const convertData = (attachFileList) => {
  // まずOCR対象のドキュメントのみにフィルタリング
  // 暫定: INVまたはPOのみを表示、かつPDFファイルのみ
  const filteredList = attachFileList.filter((item) => {
    if (!isOcrSupportedDocumentType(item)) return false;
    // INVまたはPOのみ
    if (!(item.documentLinkInv === 1 || item.documentLinkPo === 1)) return false;
    // PDFファイルのみ（.pdfで終わる）
    return item.documentName && item.documentName.toLowerCase().endsWith('.pdf');
  });

  // console.log('=== 貿易書類照合 フィルタリング結果 ===');
  // console.log(`総ドキュメント数: ${attachFileList.length}`);
  // console.log(`表示対象: ${filteredList.length}`);
  // console.log(`除外: ${attachFileList.length - filteredList.length}`);
  // console.log('=====================================');

  const processNameMap = {
    ICREQ: '輸入荷捌',
    ECREQ: '輸出荷捌',
    COPO: '発注',
    TPATPRB: '船積準備',
    FDDOC: '書類作成',
  };

  // mixinTwDocumentsAiOcr.js の btnTextByAiOcrStatus と合わせたステータスマップ
  const ocrStatusMap = {
    0: '読取開始',
    1: '読取中',
    2: '読取中',
    4: '完了',
    5: '完了',
    91: 'エラー',
    92: 'エラー',
    93: 'エラー',
    94: 'エラー',
    99: 'エラー',
  };

  return filteredList.map((item, index) => {
    // processIdからプロセス名を取得（末尾の数字を除く）
    const processIdBase = item.processId.replace(/\d+$/, '');
    const processName = processNameMap[processIdBase] || processIdBase;

    // AI-OCRサポート対象の帳票種別（INV, P/L, BL, POのみ）
    const supportedLinks = [
      'documentLinkInv',
      'documentLinkPl',
      'documentLinkBl',
      'documentLinkPo',
    ];

    // サポート対象の帳票種別で1が設定されている数をカウント
    const supportedLinkCount = supportedLinks.filter(
      (link) => item[link] === 1,
    ).length;

    // AI-OCR読取開始ボタンを表示可能かどうか
    // 条件: サポート対象の帳票種別が1つだけ1になっている
    const canStartOcr = supportedLinkCount === 1;

    // ファイル区分を判断（表示用）
    let documentCategory = '';
    if (item.documentLinkInv === 1) documentCategory = 'INV';
    else if (item.documentLinkPl === 1) documentCategory = 'P/L';
    else if (item.documentLinkBl === 1) documentCategory = 'B/L';
    else if (item.documentLinkPo === 1) documentCategory = 'P/O';
    else if (item.documentLinkSi === 1) documentCategory = 'S/I';
    else if (item.documentLinkAn === 1) documentCategory = 'A/N';
    else if (item.documentLinkCo === 1) documentCategory = 'C/O';

    // ファイルサイズをフォーマット
    const size = formatFileSize(item.documentSize);

    // 日付と時間をフォーマット（YYYY-MM-DD HH:MM）- JST表示
    const date = formatDateToJST(item.attachUpdateDateTime);

    // AI-OCR処理状況
    const aiOcrStatusCode = item.twOcrStatus; // 元のステータスコード
    const aiOcrStatus = ocrStatusMap[item.twOcrStatus] || '不明';

    return {
      id: index + 1,
      processId: item.processId,
      processName,
      documentName: item.documentName,
      date,
      size,
      aiOcrStatusCode, // 元のステータスコード（チェックボックス判定用）
      aiOcrStatus, // 表示用ステータス文字列
      documentCategory,
      canStartOcr, // AI-OCR読取開始ボタン表示可否
      editableFlg: 0,
      deletableFlg: 0,
      visibleFlg: 1,
      processProgressStatusCd: item.processProgressStatus,
      documentManageId: item.documentManageId,
      documentTrx: item.documentTrx, // PDFダウンロード用
    };
  });
};

// 書類の対応関係を定義（双方向マッピング）
const DOCUMENT_PAIR_CONFIG = {
  'P/O': ['INV'],
  INV: ['P/O'],
  // 将来的な拡張
  // 'B/L': ['P/L'],
  // 'P/L': ['B/L'],
};

export default {
  name: 'CrossCheckAiOcrList',
  inject: ['s'],
  components: {
    TwTable,
    AiOcrFileUpload,
  },
  setup() {
    const { openPdfInNewWindow } = usePdfPreview();

    return {
      openPdfInNewWindow,
    };
  },
  props: {
    apiData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isComparing: {
      type: Boolean,
      default: false,
    },
    ocrStartLoading: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  data() {
    return {
      windowHeight: window.innerHeight,
      selectedDocuments: [],
      isCompareStartEnabled: false,
      isCompareMultiStartEnabled: false,
      maxSelectableDocuments: 10,
      tableData: [],
      pdfViewMode: 'newTab', // 'newTab' or 'preview'（初期値は別タブ）
      cols: [
        {
          prop: 'selection',
          label: '',
          width: 60,
          align: 'center',
          sortable: false,
        },
        {
          prop: 'processName',
          label: 'プロセス名',
          width: 150,
          align: 'left',
          sortable: false,
          showOverflowTooltip: true,
        },
        {
          prop: 'documentName',
          label: 'ファイル名',
          width: 200,
          align: 'left',
          sortable: false,
          showOverflowTooltip: true,
        },
        {
          prop: 'documentCategory',
          label: 'ファイル区分',
          width: 120,
          align: 'center',
          sortable: false,
        },
        {
          prop: 'date',
          label: '日付',
          width: 160,
          align: 'center',
          sortable: false,
        },
        {
          prop: 'size',
          label: 'サイズ',
          width: 100,
          align: 'center',
          sortable: false,
        },
        {
          prop: 'aiOcrStatus',
          label: 'AI-OCR処理状況',
          width: 150,
          align: 'center',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    // el-alert用: 対応している組み合わせメッセージ
    supportedCombinationMessage() {
      return '＜現在対応している組み合わせ＞ P/O ↔ INV';
    },
    // 対象書類下用: 選択ガイダンスメッセージ
    // 軸（selectedDocuments[0]）は常に信頼できるため、シンプルに算出
    selectionGuidanceMessage() {
      if (this.selectedDocuments.length === 0) {
        return '';
      }

      const anchorCategory =
        this.selectedDocuments[0].documentCategory;
      const allowedPairs =
        DOCUMENT_PAIR_CONFIG[anchorCategory] || [];
      const count = this.selectedDocuments.length;
      const remaining = this.maxSelectableDocuments - count;

      if (count === 1) {
        return `${anchorCategory}（軸）が選択されています。次に ${allowedPairs.join('または')} を選択してください。`;
      }

      const nCount = count - 1;

      if (count === 2) {
        if (remaining > 0) {
          return `${anchorCategory}（軸） × ${allowedPairs[0]}: ${nCount}件 — さらに ${allowedPairs[0]} を追加できます（残り${remaining}件）。`;
        }
        return `${anchorCategory}（軸） × ${allowedPairs[0]}: ${nCount}件`;
      }

      // 3件以上
      if (remaining > 0) {
        return `${anchorCategory}（軸） × ${allowedPairs[0]}: ${nCount}件 — さらに追加できます（残り${remaining}件）。`;
      }
      return `${anchorCategory}（軸） × ${allowedPairs[0]}: ${nCount}件`;
    },
    // 複数書類比較ボタンのラベル
    // 軸（1側）は常にselectedDocuments[0]、それ以外がN側
    multiCompareButtonLabel() {
      if (this.selectedDocuments.length < 3) return '';

      const anchorCategory =
        this.selectedDocuments[0].documentCategory;
      const allowedPairs =
        DOCUMENT_PAIR_CONFIG[anchorCategory] || [];
      const nCount = this.selectedDocuments.length - 1;

      if (allowedPairs.length > 0) {
        return `${anchorCategory}と複数${allowedPairs[0]}を比較（${nCount}件の${allowedPairs[0]}）`;
      }

      return `複数書類を比較（${this.selectedDocuments.length}件）`;
    },
  },
  watch: {
    apiData: {
      handler(newData) {
        // console.log('API Data取得:', newData);
        if (newData && newData.attachFileList) {
          this.tableData = convertData(newData.attachFileList);
          // console.log('テーブルデータ変換完了:', this.tableData.length, '件');
        }
      },
      immediate: true,
    },
    // 親プロセス（Process*.vue）の documents 更新に追従して
    // 貿易書類照合一覧も再取得する。
    // NOT_STARTED ケースでは register() の emit 'save' が非同期で
    // 親の save → fetch を実行するため、handleRegistered の即時 refresh
    // だけだとファイルが反映されないことがある（race condition）。
    // 親の fetch 完了で s.res.documents の参照が変わるので、
    // それを契機に再 refresh する。
    's.res.documents'() {
      this.$emit('refresh');
    },
  },
  methods: {
    /**
     * 行が選択されているかチェック
     * @param {Object} row - 対象行
     * @returns {Boolean}
     */
    isSelected(row) {
      return this.selectedDocuments.some(
        (doc) => doc.documentManageId === row.documentManageId,
      );
    },

    /**
     * 行が選択可能かチェック
     * @param {Object} row - 対象行
     * @returns {Boolean}
     */
    isSelectable(row) {
      // 条件1: AI-OCR処理完了済み（ステータス4または5）
      const isOcrCompleted =
        row.aiOcrStatusCode === 4 || row.aiOcrStatusCode === 5;
      if (!isOcrCompleted) {
        return false;
      }

      // 条件2: 何も選択されていない場合は選択可能
      if (this.selectedDocuments.length === 0) {
        return true;
      }

      // 条件3: 既に選択されている場合は選択可能（選択解除のため）
      if (this.isSelected(row)) {
        return true;
      }

      // 条件4: 最大10件に達している場合は選択不可
      if (
        this.selectedDocuments.length >= this.maxSelectableDocuments
      ) {
        return false;
      }

      // 条件5: 軸（selectedDocuments[0]）のペアカテゴリのみ追加可能
      // 軸は解除時に全クリアされるため、常に[0]が軸として信頼できる
      const anchorCategory =
        this.selectedDocuments[0].documentCategory;
      const currentCategory = row.documentCategory;
      const allowedPairs =
        DOCUMENT_PAIR_CONFIG[anchorCategory] || [];
      return allowedPairs.includes(currentCategory);
    },

    /**
     * 選択をクリア
     */
    handleClearSelection() {
      this.selectedDocuments = [];
      this.isCompareStartEnabled = false;
      this.isCompareMultiStartEnabled = false;
    },

    /**
     * チェックボックス変更時の処理
     * @param {Object} row - 対象行
     * @param {Boolean} checked - チェック状態
     */
    handleCheckboxChange(row, checked) {
      if (checked) {
        // 選択
        if (
          this.selectedDocuments.length < this.maxSelectableDocuments
        ) {
          this.selectedDocuments.push(row);
        }
      } else {
        // 選択解除
        const isAnchor =
          this.selectedDocuments.length > 0 &&
          this.selectedDocuments[0].documentManageId ===
            row.documentManageId;
        if (isAnchor) {
          // 軸の解除 → 全選択クリア
          this.selectedDocuments = [];
          this.$message.warning(
            '軸の帳票が解除されました。再度帳票を選択してください。',
          );
        } else {
          const index = this.selectedDocuments.findIndex(
            (doc) =>
              doc.documentManageId === row.documentManageId,
          );
          if (index !== -1) {
            this.selectedDocuments.splice(index, 1);
          }
        }
      }

      // ボタンの有効化状態を更新
      this.isCompareStartEnabled = this.selectedDocuments.length === 2;
      this.isCompareMultiStartEnabled =
        this.selectedDocuments.length >= 3;

      // 2件選択されたらメッセージ表示（既存）
      if (this.selectedDocuments.length === 2) {
        const doc1 = this.selectedDocuments[0];
        const doc2 = this.selectedDocuments[1];
        this.$message.success(
          `${doc1.documentName} と ${doc2.documentName} が選択されました`,
        );
      }

      // 3件以上選択時のメッセージ
      if (this.selectedDocuments.length >= 3) {
        const anchor =
          this.selectedDocuments[0].documentCategory;
        const nCount = this.selectedDocuments.length - 1;
        const allowedPairs =
          DOCUMENT_PAIR_CONFIG[anchor] || [];
        this.$message.success(
          `${this.selectedDocuments.length}件の書類が選択されました（${anchor} × ${allowedPairs[0]}: ${nCount}件）`,
        );
      }
    },

    /**
     * 行クリック時の処理
     * @param {Object} row - クリックされた行
     * @param {Object} column - クリックされた列
     */
    handleRowClick(row, column) {
      // ファイル名列はリンクなので行選択の対象外
      if (column && column.property === 'documentName') {
        return;
      }

      // 選択不可の行はクリックしても何もしない
      if (!this.isSelectable(row)) {
        return;
      }

      // 現在の選択状態を反転
      const currentlySelected = this.isSelected(row);
      this.handleCheckboxChange(row, !currentlySelected);
    },

    /**
     * 行のクラス名を返す（カーソル制御用）
     * @param {Object} param - { row, rowIndex }
     * @returns {String} - クラス名
     */
    getRowClassName({ row }) {
      return this.isSelectable(row) ? 'selectable-row' : '';
    },

    handleClose() {
      this.$emit('close');
    },
    handleRefresh() {
      this.$emit('refresh');
    },
    handleCompareStart() {
      // 選択された書類を親に渡す（親がすべての処理を管理）
      // console.log('比較開始 - 選択書類を親に送信:', this.selectedDocuments);
      this.$emit('compare-start', this.selectedDocuments);
    },
    handleCompareMultiStart() {
      // 複数書類比較を親に通知
      this.$emit('compare-multi-start', this.selectedDocuments);
    },
    async handlePreviewPdf(row) {
      try {
        await this.openPdfInNewWindow(
          row.documentManageId,
          row.documentTrx,
          row.documentName,
        );
      } catch (err) {
        this.$message.error('PDFの取得に失敗しました');
      }
    },
    handleStartAiOcr(row) {
      this.$emit('start-ai-ocr', row);
    },
    getOcrStatusTagType(status) {
      const typeMap = {
        '読取開始': 'info',
        '読取中': 'warning',
        '完了': 'success',
        'エラー': 'danger',
      };
      return typeMap[status] || 'info';
    },

    onWindowResize() {
      this.windowHeight = window.innerHeight;
    },
    handleRegistered() {
      this.$emit('refresh');
      this.$nextTick(() => {
        const drawerBody = this.$el.querySelector('.drawer-body');
        if (drawerBody) drawerBody.scrollTop = 0;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.drawer-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e4e7ed;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
}

.content-area {
  width: 100%;
}


</style>

<style lang="scss">
// 行のカーソル制御
.el-table__row {
  cursor: default !important;
}

.el-table__row.selectable-row {
  cursor: pointer !important;

  &:hover {
    background-color: #f5f7fa;
  }
}

// ファイル名リンクのスタイル
.document-name-link {
  color: #409eff;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #66b1ff;
  }
}
</style>
